import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Music2, Pause, Play } from 'lucide-react';

type MusicPlayerContextValue = {
  isPlaying: boolean;
  musicTime: number;
  musicDuration: number;
  toggleMusic: () => void;
  seekMusic: (value: number) => void;
  play: () => void;
  pause: () => void;
  requestAutoplay: () => void;
  setInvitationVisible: (visible: boolean) => void;
};

const playRetryDelays = [0, 300, 800, 1500, 2500];

const MusicPlayerContext = createContext<MusicPlayerContextValue | null>(null);

const musicDuration = 224;
const scrollRevealThreshold = 160;

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicTime, setMusicTime] = useState(0);
  const [invitationVisible, setInvitationVisible] = useState(() => window.location.pathname === '/');
  const [scrolled, setScrolled] = useState(false);
  const [autoplayPending, setAutoplayPending] = useState(false);
  const musicFrame = useRef<HTMLIFrameElement>(null);
  const playRetryTimeouts = useRef<number[]>([]);
  const location = useLocation();

  const youtubeCommand = (func: string, args: number[] = []) => {
    musicFrame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
  };

  useEffect(() => {
    if (!isPlaying) return;
    const timer = window.setInterval(() => setMusicTime((value) => (value >= musicDuration ? 0 : value + 1)), 1000);
    return () => window.clearInterval(timer);
  }, [isPlaying]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > scrollRevealThreshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const clearPlayRetries = () => {
    playRetryTimeouts.current.forEach((id) => window.clearTimeout(id));
    playRetryTimeouts.current = [];
  };

  const play = () => {
    setIsPlaying(true);
    // O iframe pode ainda não ter terminado de inicializar logo após montar/recarregar a página,
    // então reenviamos o comando algumas vezes para garantir que ele seja recebido.
    clearPlayRetries();
    playRetryTimeouts.current = playRetryDelays.map((delay) => window.setTimeout(() => youtubeCommand('playVideo'), delay));
  };
  const pause = () => {
    // Cancela qualquer reenvio de "tocar" pendente, senão a música pode voltar a tocar sozinha logo após pausar.
    clearPlayRetries();
    youtubeCommand('pauseVideo');
    setIsPlaying(false);
  };
  const toggleMusic = () => (isPlaying ? pause() : play());
  const seekMusic = (value: number) => { setMusicTime(value); youtubeCommand('seekTo', [value, 1]); };

  // Navegadores bloqueiam áudio automático sem interação do usuário. Tentamos tocar
  // imediatamente e, se o navegador bloquear, retomamos no primeiro toque/clique/scroll.
  const requestAutoplay = () => {
    play();
    setAutoplayPending(true);
  };

  useEffect(() => {
    if (!autoplayPending) return;
    const resume = () => { play(); setAutoplayPending(false); };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;
    events.forEach((eventName) => window.addEventListener(eventName, resume, { passive: true }));
    return () => events.forEach((eventName) => window.removeEventListener(eventName, resume));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayPending]);

  const isHome = location.pathname === '/';
  const showMiniPlayer = !invitationVisible && (!isHome || scrolled);

  return (
    <MusicPlayerContext.Provider value={{ isPlaying, musicTime, musicDuration, toggleMusic, seekMusic, play, pause, requestAutoplay, setInvitationVisible }}>
      {children}
      {showMiniPlayer && (
        <div className="music-player-mini" aria-label="Player da música da Valentina">
          <Music2 size={17} />
          <span className="music-meta"><small>NOSSA MÚSICA</small><strong>Música da Valentina</strong></span>
          <input
            aria-label="Progresso da música"
            type="range"
            min="0"
            max={musicDuration}
            value={musicTime}
            onChange={(event) => seekMusic(Number(event.target.value))}
            style={{ '--music-progress': `${(musicTime / musicDuration) * 100}%` } as React.CSSProperties}
          />
          <button type="button" onClick={toggleMusic} aria-label={isPlaying ? 'Pausar música' : 'Tocar música'}>
            {isPlaying ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
          </button>
        </div>
      )}
      <iframe
        ref={musicFrame}
        className="music-source"
        src="https://www.youtube.com/embed/I19_lMP9NBw?enablejsapi=1&controls=0&playsinline=1&rel=0"
        title="Fonte de áudio da música da Valentina"
        allow="autoplay; encrypted-media"
      />
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer deve ser usado dentro de MusicPlayerProvider');
  return ctx;
}
